import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { Processing } from '@/api/entities/processing';
import {
  EngineCorrectionReportType,
  EngineCorrectionReportDataType,
} from '@/api/entities/engineCorrection';
import { Rectangle, Polygon } from '@/entities/canvas';
import { Options } from '@/components/Controls/Select';
import { COMPONENT_KEY, DEFECT_COMPONENT_KEY } from '@/utils/constant.utils';
import annotationData from '@/data/annotation.json';
import { BoundingBox } from '@/type/detection.types';
import { TFunction } from 'i18next';
import { getSortedList } from '@/hooks/list-order';

export const formatDmgValue = (t: TFunction<undefined>, value: string) =>
  `${value.split('_')[0]} (${t('common:level_short')} ${value.split('_')[1]})`;

export const getLabelByValue = (
  t: TFunction<undefined>,
  value,
  isElement: boolean,
  equipmentOptions,
  damageOptions
) => {
  if (!value) return null;
  const options = isElement ? [...equipmentOptions] : [...damageOptions];
  const option = options.find((el) => el.value === value);
  if (isElement) return option ? option.label : null;
  else if (!isElement) return formatDmgValue(t, option.label);
  return null;
};

export const getLabelValueDefault = (
  value,
  isElement: boolean,
  equipmentOptions,
  damageOptions
) => {
  if (!value) return null;
  const options = isElement ? [...equipmentOptions] : [...damageOptions];
  const option = options.find((el) => el.value === value);
  return option ? option.label : null;
};

export const getColorByValue = (
  value,
  isElement: boolean,
  equipmentOptions,
  damageOptions
) => {
  if (!value) return null;
  const options = isElement ? [...equipmentOptions] : [...damageOptions];
  const option = options.find((el) => el.value === value);
  return option ? option.color : null;
};

export const getDmgValue = (label: string, value: string, damageOptions) => {
  const dmgValue = damageOptions.find(
    (dmg) => dmg.label === `${label}_${value}`
  )?.value;
  return dmgValue;
};

export const getDmgLabelByValue = (value: string, damageOptions) => {
  const dmgLabel = damageOptions.find((dmg) => dmg.value === value);
  return dmgLabel;
};

export const getEquipmentValue = (label: string, equipmentOptions) => {
  const equipmentValue = equipmentOptions.find(
    (equipment) => equipment.label === label
  )?.value;
  return equipmentValue;
};

export const getDmgValueByLabel = (label: string, damageOptions) => {
  const dmgValue = damageOptions.find((dmg) => dmg.label === label)?.value;
  return dmgValue;
};

export const getAnnotation = (): TResultImageCoordinatesSchema[] => {
  const data = annotationData as unknown as TResultImageCoordinatesSchema[];

  const dataMapped: TResultImageCoordinatesSchema[] = data.map((d) => ({
    elNumber: d.elNumber,
    xCenter: d.xCenter,
    yCenter: d.yCenter,
    width: d.width,
    height: d.height,
  }));

  return dataMapped;
};

export const convertToYOLO = (
  annotations: TResultImageCoordinatesSchema[]
): string => {
  return annotations
    .map((annotation) => {
      const classId = annotation.elNumber;
      const xCenter = annotation.xCenter;
      const yCenter = annotation.yCenter;
      const width = annotation.width;
      const height = annotation.height;
      return `${classId} ${xCenter} ${yCenter} ${Math.abs(width)} ${Math.abs(height)}`;
    })
    .join('\n');
};

export const convertToYOLOSeg = (
  annotations: TResultImageCoordinatesSchema[]
): string => {
  return annotations
    .map((annotation) => {
      const classId = annotation.elNumber;
      const coordinates = annotation.point.flatMap((p) => [
        Math.abs(p.x),
        Math.abs(p.y),
      ]);
      return [classId, ...coordinates].join(' ');
    })
    .join('\n');
};

export const getFileToUpload = (
  annotation: TResultImageCoordinatesSchema[],
  dataFile: Processing,
  isDmg: boolean
) => {
  const yoloData = isDmg
    ? convertToYOLOSeg(annotation)
    : convertToYOLO(annotation);
  const fileName = dataFile.fileName;
  const blob = new Blob([yoloData], { type: 'text/plain' });
  const fileToUpload = new File([blob], `${fileName}.txt`, {
    type: 'text/plain',
  });
  return fileToUpload;
};

export const initReport = (id: string): EngineCorrectionReportType => ({
  idProcessing: id,
  isCorrectElements: true,
  isCorrectDamages: true,
  elmtCorrectionTypes: null,
  elmtBeforeCorrection: null,
  elmtAfterCorrection: null,
  dmgCorrectionTypes: null,
  dmgBeforeCorrection: null,
  dmgAfterCorrection: null,
  linkElDmgCorrectionType: null,
  linkElDmgBeforeCorrectionEl: null,
  linkElDmgAfterCorrectionEl: null,
  linkElDmgBeforeCorrectionDmg: null,
  linkElDmgAfterCorrectionDmg: null,
  dmgLevelBeforeCorrection: null,
  dmgLevelAfterCorrection: null,
});

export const forceInitReport = (
  id: string
): EngineCorrectionReportDataType => ({
  idReport: id,
  edit: [],
  create: [initReport(id)],
  remove: [],
});

export const initReportEngine = (
  id: string
): EngineCorrectionReportDataType => ({
  idReport: id,
  edit: [],
  create: [],
  remove: [],
});

export const convertRectangePointToYolo = (
  elNumber,
  rect: Rectangle,
  polygon: Polygon,
  canvasRef: React.RefObject<HTMLCanvasElement>,
  isRect = true
): TResultImageCoordinatesSchema => {
  const canvas = canvasRef.current;
  if (!canvas) {
    console.error('Canvas non disponible');
    return null;
  }

  if (isRect) {
    const width = rect?.endX - rect?.startX;
    const height = rect?.endY - rect?.startY;
    const xCenter = rect?.startX + width / 2;
    const yCenter = rect?.startY + height / 2;

    return {
      elNumber,
      xCenter: xCenter / canvas.width,
      yCenter: yCenter / canvas.height,
      width: width / canvas.width,
      height: height / canvas.height,
    };
  }

  if (!isRect) {
    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;
    const normalizedPoints = polygon.map((point) => ({
      x: point.x / canvasWidth,
      y: point.y / canvasHeight,
    }));

    return {
      elNumber,
      point: normalizedPoints,
    };
  }

  return null;
};

export const convertSegToCanvasData = (data: number, canvasWOrH) => {
  return data * canvasWOrH;
};

export const getScaledCoordinates = (
  x: number,
  y: number,
  canvasRef: React.RefObject<HTMLCanvasElement>
) => {
  const canvas = canvasRef.current;
  if (!canvas) return { x, y };

  const rect = canvas.getBoundingClientRect();

  const scaleX = canvas.width / rect.width; // Ratio de largeur
  const scaleY = canvas.height / rect.height; // Ratio de hauteur

  return {
    x: (x - rect.left) * scaleX,
    y: (y - rect.top) * scaleY,
    rect,
  };
};

export const convertRectangleToYOLORatio = (
  classId: number,
  rectangle: Rectangle,
  canvasRef: React.RefObject<HTMLCanvasElement>
): TResultImageCoordinatesSchema => {
  const canvas = canvasRef.current;
  if (!canvas) {
    console.error('Canvas non disponible');
    return null;
  }

  const { startX, startY, endX, endY } = rectangle;

  // Mise à l'échelle des coordonnées en utilisant getScaledCoordinates
  const scaledStart = getScaledCoordinates(startX, startY, canvasRef);
  const scaledEnd = getScaledCoordinates(endX, endY, canvasRef);

  if (!scaledStart || !scaledEnd) {
    console.error("Impossible de calculer les coordonnées mises à l'échelle");
    return null;
  }

  // Calcul des dimensions mises à l'échelle
  const rectWidth = scaledEnd.x - scaledStart.x;
  const rectHeight = scaledEnd.y - scaledStart.y;

  // Calcul des coordonnées du centre
  const xCenter = (scaledStart.x + rectWidth / 2) / canvas.width;
  const yCenter = (scaledStart.y + rectHeight / 2) / canvas.height;

  // Conversion en fractions de la taille totale
  const normalizedWidth = rectWidth / canvas.width;
  const normalizedHeight = rectHeight / canvas.height;

  // Retourner les données YOLO
  return {
    elNumber: classId.toString(),
    xCenter: Number(xCenter.toFixed(6)),
    yCenter: Number(yCenter.toFixed(6).toString()),
    width: Number(normalizedWidth.toFixed(6)),
    height: Number(normalizedHeight.toFixed(6)),
  };
};

export const isElInsideEl = (
  inner: TResultImageCoordinatesSchema,
  outer: TResultImageCoordinatesSchema
): boolean => {
  // Calculate bounds of inner element
  const innerStartX = inner.xCenter - inner.width / 2;
  const innerStartY = inner.yCenter - inner.height / 2;
  const innerEndX = inner.xCenter + inner.width / 2;
  const innerEndY = inner.yCenter + inner.height / 2;

  // Calculate bounds of outer element
  const outerStartX = outer.xCenter - outer.width / 2;
  const outerStartY = outer.yCenter - outer.height / 2;
  const outerEndX = outer.xCenter + outer.width / 2;
  const outerEndY = outer.yCenter + outer.height / 2;

  // Check if all corners of the inner element are within the outer element
  return (
    innerStartX >= outerStartX &&
    innerEndX <= outerEndX &&
    innerStartY >= outerStartY &&
    innerEndY <= outerEndY
  );
};

export const findElementParentDmg = (
  dmg: TResultImageCoordinatesSchema,
  listEl: TResultImageCoordinatesSchema[]
): TResultImageCoordinatesSchema | null => {
  let parent: TResultImageCoordinatesSchema | null = null;
  for (const el of listEl) {
    // if (el.elNumber === dmg.elNumber) continue; // Skip the same element

    if (isElInsideEl(dmg, el)) {
      // If dmg is inside el, check if it's the smallest enclosing parent
      if (!parent || isElInsideEl(el, parent)) {
        parent = el;
      }
    }
  }

  return parent;
};

export const findDmgInsideEl = (
  listDmg: TResultImageCoordinatesSchema[],
  el: TResultImageCoordinatesSchema
): TResultImageCoordinatesSchema[] =>
  listDmg.filter((dmg) => isElInsideEl(dmg, el));

export const mapComponentOptions = (
  listComponentKey: string[] = [],
  type: string,
  t: TFunction<undefined>
): Options[] => {
  const isEquipment = type === '1';
  const listOptions: Options = {
    label: isEquipment ? t('common:allEquipments') : t('common:allDamages'),
    value: '',
  };

  let mapped = listComponentKey?.map((key) => ({
    label:
      (type === '1' ? COMPONENT_KEY[key] : DEFECT_COMPONENT_KEY[key]) ?? key,
    value: key,
  }));

  mapped = getSortedList(mapped, 'label', 'asc');

  return [listOptions, ...mapped];
};

export const loadDetectionData = async (
  filePath: string
): Promise<BoundingBox[]> => {
  try {
    const response = await fetch(filePath);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = await response.text();

    // Early return if file is empty
    if (!text.trim()) {
      return [];
    }

    return text
      .split('\n')
      .filter((line) => line.trim() !== '')
      .map((line) => {
        const basicParts = line.split(',');

        // Skip malformed lines
        if (basicParts.length < 9) {
          console.warn(`Skipping malformed line: ${line}`);
          return null;
        }

        const video_size = basicParts[0];
        const timestamp = parseFloat(basicParts[1]);
        const classId = parseInt(basicParts[2]);
        const className = basicParts[3];
        const confidence = parseFloat(basicParts[4]);
        const x1 = parseFloat(basicParts[5]);
        const y1 = parseFloat(basicParts[6]);
        const x2 = parseFloat(basicParts[7]);
        const y2 = parseFloat(basicParts[8]);

        const boundingBox: BoundingBox = {
          video_size,
          timestamp,
          classId,
          className,
          confidence,
          x1,
          y1,
          x2,
          y2,
        };

        if (filePath.includes('damage.txt')) {
          const polygonPart = line.substring(
            line.indexOf(basicParts[8]) + basicParts[8].length
          );

          if (polygonPart.includes(';')) {
            const polygonString = polygonPart.startsWith(',')
              ? polygonPart.substring(1)
              : polygonPart;
            const pointPairs = polygonString.split(';');

            const polygonPoints = pointPairs.map((pair) => {
              const [x, y] = pair.split(',');
              return {
                x: parseFloat(x),
                y: parseFloat(y),
              };
            });

            boundingBox.polygon_points = polygonPoints;
          }
        }

        return boundingBox;
      })
      .filter((box): box is BoundingBox => box !== null); // Filter out null values
  } catch (error) {
    console.error(`Error loading data from ${filePath}:`, error);
    return [];
  }
};
